import React, { useRef, useState, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Video } from 'lucide-react';

const publicPath = (p) => (process.env.PUBLIC_URL || '') + p;

const videos = [
  { src: publicPath('/assets/videos/v1.mp4'), poster: publicPath('/assets/videos/v1.jpg'), name: 'Parent Review', tag: 'CBSE Class 8', accent: '#E64B2B' },
  { src: publicPath('/assets/videos/v2.mp4'), poster: publicPath('/assets/videos/v2.jpg'), name: 'Parent Review', tag: 'Class 6 • Maths', accent: '#4FB3B8' },
  { src: publicPath('/assets/videos/v3.mp4'), poster: publicPath('/assets/videos/v3.jpg'), name: 'Parent Review', tag: 'Board Prep',   accent: '#F4B93E' },
  { src: publicPath('/assets/videos/v4.mp4'), poster: publicPath('/assets/videos/v4.jpg'), name: 'Parent Review', tag: 'CBSE Class 5', accent: '#7BB7DC' },
];

function VideoCard({ video }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  }, []);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  }, []);

  return (
    <div className="relative bg-white border-2 border-[#2C3E50] rounded-3xl overflow-hidden paper-shadow-sm group hover:-translate-y-1">
      <div className="relative bg-[#2C3E50]">
        <video
          ref={ref}
          src={video.src}
          poster={video.poster}
          className="w-full h-72 object-cover"
          playsInline
          muted
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
        {!playing && (
          <button
            type="button"
            aria-label="Play video"
            onClick={toggle}
            className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/10"
          >
            <span className="w-16 h-16 rounded-full bg-white text-[#E64B2B] border-2 border-[#2C3E50] flex items-center justify-center paper-shadow-sm">
              <Play size={26} strokeWidth={3} fill="#E64B2B" />
            </span>
          </button>
        )}
        <div
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white border-2 border-white"
          style={{ background: video.accent }}
        >
          <Video size={11} strokeWidth={3} /> Testimonial
        </div>
        {playing && (
          <div className="absolute bottom-3 right-3 flex gap-2">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="w-9 h-9 rounded-full bg-white/95 text-[#2C3E50] border-2 border-[#2C3E50] flex items-center justify-center hover:bg-[#F4B93E]"
            >
              {muted ? <VolumeX size={16} strokeWidth={3} /> : <Volume2 size={16} strokeWidth={3} />}
            </button>
            <button
              type="button"
              onClick={toggle}
              aria-label="Pause"
              className="w-9 h-9 rounded-full bg-white/95 text-[#2C3E50] border-2 border-[#2C3E50] flex items-center justify-center hover:bg-[#F4B93E]"
            >
              <Pause size={16} strokeWidth={3} />
            </button>
          </div>
        )}
      </div>
      <div className="p-4 flex items-center justify-between gap-3">
        <div>
          <div className="font-black text-[#2C3E50]" style={{ fontFamily: 'Fraunces, serif' }}>{video.name}</div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[#2C3E50]/60">{video.tag}</div>
        </div>
        <button
          type="button"
          onClick={toggle}
          className="px-3 py-1.5 rounded-md bg-[#F4B93E] text-[#2C3E50] font-black text-xs uppercase tracking-widest border-2 border-[#2C3E50] hover:-translate-y-0.5"
        >
          {playing ? 'Pause' : 'Watch'}
        </button>
      </div>
    </div>
  );
}

export default function VideoTestimonials() {
  return (
    <div className="mb-14">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <span className="inline-flex items-center gap-2 bg-[#E64B2B] text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border-2 border-[#2C3E50]">
          <Video size={12} strokeWidth={3} /> Video reviews from real parents
        </span>
        <h3 className="mt-3 text-2xl lg:text-3xl font-black text-[#2C3E50] leading-tight" style={{ fontFamily: 'Fraunces, serif' }}>
          Hear it in <span className="text-[#4FB3B8]">their own words.</span>
        </h3>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {videos.map((v) => (
          <VideoCard key={v.src} video={v} />
        ))}
      </div>
    </div>
  );
}
